import { DOCUMENT } from '@angular/common';
import * as i0 from '@angular/core';
import { Inject, Injectable, NgModule, Pipe } from '@angular/core';
import * as i1 from '@angular/router';
import { NavigationStart, NavigationError } from '@angular/router';
import { filter, skip, map, takeUntil } from 'rxjs/operators';
import { HTTP_INTERCEPTORS, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import * as Globalize from 'globalize';
import { BehaviorSubject, Subject } from 'rxjs';

class AppStabilityService {
    document;
    router;
    window;
    CHUNK_LOAD_ERROR_RECOVERY_URL_KEY = 'ChunkLoadError_recovery_url';
    constructor(document, router) {
        this.document = document;
        this.router = router;
        this.window = this.document?.defaultView;
    }
    init() {
        this.router.events
            .pipe(filter((event) => event instanceof NavigationStart || event instanceof NavigationError))
            .subscribe((event) => {
            if (event instanceof NavigationStart) {
                this.recoverFromNavigationError(event);
            }
            else if (event instanceof NavigationError) {
                this.handleChunkLoadErrorDuringNavigation(event);
            }
        });
    }
    /**
     * This handler should be used with `NavigationError` events in a `RouterEvent` navigation interceptor.
     *
     * The companion method `recoverFromNavigationError` should be used with the `NavigationStart` event.
     *
     * -- `ChunkLoadErrors` --
     *
     * When the page is left open in the browser for an extended time, it's possible that the user
     * may return to the open page after a deployment of new bundles.
     *
     * The page loaded in their browser can become stale with references to a lazy-loaded
     * javascript bundle (chunk) that has been replaced by a newer deployment bundle.
     *
     * When the browser tries to request the missing bundle, we get a `ChunkLoadError`. Since the page,
     * an HTML doc, is not cached and the javascript bundle has a cache-busting hash in its filename,
     * a full page reload will often resolve the problem.
     *
     * Here, we are assuming that `ChunkLoadError`s occur during navigation to a lazy-loaded feature module.
     */
    handleChunkLoadErrorDuringNavigation(event) {
        if (this.isSessionStorageEnabled()) {
            if (this.isChunkLoadError(event)) {
                if (this.isUrlSameAsStoredChunkLoadErrorUrl(event.url)) {
                    // Houston, we've had a problem..
                    this.window.console.log(`ChunkLoadError for same route after reload: ${event.url}`);
                }
                else {
                    this.window.sessionStorage.setItem(this.CHUNK_LOAD_ERROR_RECOVERY_URL_KEY, event.url);
                    this.window.location.reload();
                }
            }
        }
    }
    /**
     * This method should be used with `NavigationStart` events in a `RouterEvent` navigation interceptor.
     *
     * The companion method `handleChunkLoadErrorDuringNavigation` should be used with the `NavigationError` event.
     */
    recoverFromNavigationError(event) {
        if (this.isSessionStorageEnabled()) {
            if (this.window.sessionStorage.getItem(this.CHUNK_LOAD_ERROR_RECOVERY_URL_KEY) !== null) {
                const recoveryUrl = String(this.window.sessionStorage.getItem(this.CHUNK_LOAD_ERROR_RECOVERY_URL_KEY));
                this.clearChunkLoadErrorUrl();
                if (!this.isUrlSameAsStoredChunkLoadErrorUrl(event.url)) {
                    // assume we've reloaded the page with references to the latest chunks..
                    this.router.navigateByUrl(recoveryUrl);
                }
            }
        }
    }
    isSessionStorageEnabled = () => {
        try {
            const key = `__storage__test`;
            this.window.sessionStorage.setItem(key, '42');
            this.window.sessionStorage.removeItem(key);
            return true;
        }
        catch {
            return false;
        }
    };
    isUrlSameAsStoredChunkLoadErrorUrl(url) {
        return this.window.sessionStorage.getItem(this.CHUNK_LOAD_ERROR_RECOVERY_URL_KEY) === url;
    }
    clearChunkLoadErrorUrl() {
        this.window.sessionStorage.removeItem(this.CHUNK_LOAD_ERROR_RECOVERY_URL_KEY);
    }
    isChunkLoadError(event) {
        return /ChunkLoadError/.test(event.error);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AppStabilityService, deps: [{ token: DOCUMENT }, { token: i1.Router }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AppStabilityService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: AppStabilityService, decorators: [{
            type: Injectable,
            args: [{
                    providedIn: 'root'
                }]
        }], ctorParameters: () => [{ type: Document, decorators: [{
                    type: Inject,
                    args: [DOCUMENT]
                }] }, { type: i1.Router }] });

var CREDIT_CARDS;
(function (CREDIT_CARDS) {
    CREDIT_CARDS["None"] = "none";
    CREDIT_CARDS["Visa"] = "visa";
    CREDIT_CARDS["Mastercard"] = "mast";
    CREDIT_CARDS["Discover"] = "disc";
    CREDIT_CARDS["AmericanExpress"] = "amex";
    CREDIT_CARDS["JapanCreditBureau"] = "jcb";
    CREDIT_CARDS["ChinaUnionPay"] = "cup";
    CREDIT_CARDS["DinersClub"] = "diners";
})(CREDIT_CARDS || (CREDIT_CARDS = {}));

class CreditCardValidationService {
    static IsValidCreditCardNumber(creditCardNumber, creditCardBinRanges) {
        // First make sure the number passes the Luhn check
        if (!CreditCardValidationService.passesLuhnCheck(creditCardNumber)) {
            return false;
        }
        return CreditCardValidationService.GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges).IsValid;
    }
    static GetCreditCardType(creditCardNumber, creditCardBinRanges) {
        // First get the bin range
        const binRange = CreditCardValidationService.GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges, true);
        if (binRange.IsValid) {
            if (binRange.CardType) {
                return binRange.CardType;
            }
            else {
                return CREDIT_CARDS.None;
            }
        }
        return CREDIT_CARDS.None;
    }
    static GetBinRangeForCreditCard(creditCardNumber, creditCardBinRanges, requiredCardTypeOnly = false) {
        const formattedNumber = creditCardNumber.replace(/\D+/g, '');
        const possibleBinRanges = creditCardBinRanges?.ValidBinRanges.filter((binRange) => {
            const binToVerify = formattedNumber.substring(0, binRange.BinLength);
            if (Number.isNaN(binToVerify)) {
                return false;
            }
            const ccBinNumber = Number.parseInt(binToVerify, 10);
            const isValidBin = ccBinNumber >= Number.parseInt(binRange.From, 10) && ccBinNumber <= Number.parseInt(binRange.To, 10);
            if (requiredCardTypeOnly) {
                return isValidBin;
            }
            else {
                const isValidLength = formattedNumber.length >= binRange.CreditCardMinLength && formattedNumber.length <= binRange.CreditCardMaxLength;
                return isValidBin && isValidLength;
            }
        });
        if (possibleBinRanges && possibleBinRanges.length === 1) {
            return {
                IsValid: true,
                CardType: possibleBinRanges[0].CreditCardTypeString
            };
        }
        return {
            IsValid: false,
        };
    }
    // Logic stolen from old Resident portal and Luhn logic here https://en.wikipedia.org/wiki/Luhn_algorithm
    static passesLuhnCheck(creditCardNumber) {
        const formattedNumber = creditCardNumber.replace(/\D+/g, '');
        // Make sure the numbers aren't all 0s
        let nonZeroFound = false, nCheck = 0, bEven = false;
        for (let cardNumberIndex = formattedNumber.length - 1; cardNumberIndex >= 0; cardNumberIndex--) {
            const cDigit = formattedNumber.charAt(cardNumberIndex);
            let nDigit = parseInt(cDigit, 10);
            if (nDigit !== 0) {
                nonZeroFound = true;
            }
            if (bEven) {
                // eslint-disable-next-line no-cond-assign
                if ((nDigit *= 2) > 9) {
                    nDigit -= 9;
                }
            }
            nCheck += nDigit;
            bEven = !bEven;
        }
        if (!nonZeroFound) {
            return false;
        }
        return (nCheck % 10) === 0;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CreditCardValidationService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CreditCardValidationService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: CreditCardValidationService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

class RequestInterceptor {
    intercept(req, next) {
        let sanitizedRequest = req;
        sanitizedRequest = this.sanitizeParams(sanitizedRequest);
        sanitizedRequest = this.sanitizeHeaders(sanitizedRequest);
        sanitizedRequest = this.sanitizeQueryString(sanitizedRequest);
        return next.handle(sanitizedRequest);
    }
    sanitizeHeaders(req) {
        let { headers } = req;
        req.headers.keys().forEach(key => {
            const value = req.headers.get(key);
            if (value === '' || value === 'null' || value === 'undefined') {
                headers = headers.delete(key);
            }
        });
        return req.clone({ headers });
    }
    sanitizeParams(req) {
        if (!req.params) {
            return req;
        }
        let { params } = req;
        req.params.keys().forEach(key => {
            const value = req.params.get(key);
            if (value === '' || value === 'null' || value === 'undefined') {
                params = params.delete(key);
            }
        });
        return req.clone({ params });
    }
    sanitizeQueryString(req) {
        const queryIndex = req.url.indexOf('?');
        if (queryIndex < 0) {
            return req;
        }
        const path = req.url.substring(0, queryIndex);
        const queryString = req.url.substring(queryIndex + 1)
            .split('&')
            .filter(param => param.indexOf('=') !== param.length - 1)
            .filter(param => !param.endsWith('=null') && !param.endsWith('=undefined'))
            .join('&');
        return req.clone({ url: `${path}?${queryString}` });
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RequestInterceptor, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RequestInterceptor });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: RequestInterceptor, decorators: [{
            type: Injectable
        }] });

class BdxHttpModule {
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxHttpModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule });
    static ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: BdxHttpModule });
    static ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxHttpModule, providers: [
            {
                provide: HTTP_INTERCEPTORS,
                useClass: RequestInterceptor,
                multi: true
            },
            provideHttpClient(withInterceptorsFromDi())
        ] });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxHttpModule, decorators: [{
            type: NgModule,
            args: [{
                    imports: [],
                    providers: [
                        {
                            provide: HTTP_INTERCEPTORS,
                            useClass: RequestInterceptor,
                            multi: true
                        },
                        provideHttpClient(withInterceptorsFromDi())
                    ]
                }]
        }] });

// All of the currency codes listed were found here https://en.wikipedia.org/wiki/ISO_4217
const CultureDefinitions = new Map([
    ['af-ZA', { Id: 1, Text: 'Afrikaans (South Africa)', Name: 'af-ZA', CurrencyCode: 'ZAR' }],
    ['sq-AL', { Id: 2, Text: 'Albanian (Albania)', Name: 'sq-AL', CurrencyCode: 'ALL' }],
    ['ar-DZ', { Id: 3, Text: 'Arabic (Algeria)', Name: 'ar-DZ', CurrencyCode: 'DZD' }],
    ['ar-BH', { Id: 4, Text: 'Arabic (Bahrain)', Name: 'ar-BH', CurrencyCode: 'BHD' }],
    ['ar-EG', { Id: 5, Text: 'Arabic (Egypt)', Name: 'ar-EG', CurrencyCode: 'EGP' }],
    ['ar-IQ', { Id: 6, Text: 'Arabic (Iraq)', Name: 'ar-IQ', CurrencyCode: 'IQD' }],
    ['ar-JO', { Id: 7, Text: 'Arabic (Jordan)', Name: 'ar-JO', CurrencyCode: 'JOD' }],
    ['ar-KW', { Id: 8, Text: 'Arabic (Kuwait)', Name: 'ar-KW', CurrencyCode: 'KWD' }],
    ['ar-LB', { Id: 9, Text: 'Arabic (Lebanon)', Name: 'ar-LB', CurrencyCode: 'LBP' }],
    ['ar-LY', { Id: 10, Text: 'Arabic (Libya)', Name: 'ar-LY', CurrencyCode: 'LYD' }],
    ['ar-MA', { Id: 11, Text: 'Arabic (Morocco)', Name: 'ar-MA', CurrencyCode: 'MAD' }],
    ['cu-MA', { Id: 11, Text: 'Arabic (Morocco)', Name: 'cu-MA', CurrencyCode: 'MAD', MappedLocale: 'ar-MA' }],
    ['ar-OM', { Id: 12, Text: 'Arabic (Oman)', Name: 'ar-OM', CurrencyCode: 'OMR' }],
    ['ar-QA', { Id: 13, Text: 'Arabic (Qatar)', Name: 'ar-QA', CurrencyCode: 'QAR' }],
    ['ar-SA', { Id: 14, Text: 'Arabic (Saudi Arabia)', Name: 'ar-SA', CurrencyCode: 'SAR' }],
    ['ar-SY', { Id: 15, Text: 'Arabic (Syria)', Name: 'ar-SY', CurrencyCode: 'SYP' }],
    ['ar-TN', { Id: 16, Text: 'Arabic (Tunisia)', Name: 'ar-TN', CurrencyCode: 'TND' }],
    ['ar-AE', { Id: 17, Text: 'Arabic (U.A.E.)', Name: 'ar-AE', CurrencyCode: 'AED' }],
    ['cu-AE', { Id: 18, Text: 'Arabic (U.A.E.) - Custom', Name: 'cu-AE', CurrencyCode: 'AED', MappedLocale: 'ar-AE' }],
    ['ar-YE', { Id: 19, Text: 'Arabic (Yemen)', Name: 'ar-YE', CurrencyCode: 'YER' }],
    ['hy-AM', { Id: 20, Text: 'Armenian (Armenia)', Name: 'hy-AM', CurrencyCode: 'AMD' }],
    ['eu-ES', { Id: 21, Text: 'Basque (Basque)', Name: 'eu-ES', CurrencyCode: 'EUR' }],
    ['be-BY', { Id: 22, Text: 'Belarusian (Belarus)', Name: 'be-BY', CurrencyCode: 'BYN' }],
    ['ca-ES', { Id: 23, Text: 'Catalan (Catalan)', Name: 'ca-ES', CurrencyCode: 'EUR' }],
    ['zh-HK', { Id: 24, Text: 'Chinese (Hong Kong S.A.R.)', Name: 'zh-HK', CurrencyCode: 'HKD' }],
    ['zh-MO', { Id: 25, Text: 'Chinese (Macau S.A.R.)', Name: 'zh-MO', CurrencyCode: 'MOP' }],
    ['zh-CN', { Id: 26, Text: 'Chinese (People\'s Republic of China)', Name: 'zh-CN', CurrencyCode: 'CNY' }],
    ['zh-SG', { Id: 27, Text: 'Chinese (Singapore)', Name: 'zh-SG', CurrencyCode: 'SGD' }],
    ['zh-TW', { Id: 28, Text: 'Chinese (Taiwan)', Name: 'zh-TW', CurrencyCode: 'TWD' }],
    ['hr-HR', { Id: 29, Text: 'Croatian (Croatia)', Name: 'hr-HR', CurrencyCode: 'HRK' }],
    ['cs-CZ', { Id: 30, Text: 'Czech (Czech Republic)', Name: 'cs-CZ', CurrencyCode: 'CZK' }],
    ['da-DK', { Id: 31, Text: 'Danish (Denmark)', Name: 'da-DK', CurrencyCode: 'DKK' }],
    ['nl-BE', { Id: 32, Text: 'Dutch (Belgium)', Name: 'nl-BE', CurrencyCode: 'EUR' }],
    ['nl-NL', { Id: 33, Text: 'Dutch (Netherlands)', Name: 'nl-NL', CurrencyCode: 'EUR' }],
    ['en-AU', { Id: 34, Text: 'English (Australia)', Name: 'en-AU', CurrencyCode: 'AUD' }],
    ['en-BZ', { Id: 35, Text: 'English (Belize)', Name: 'en-BZ', CurrencyCode: 'BZD' }],
    ['en-CA', { Id: 36, Text: 'English (Canada)', Name: 'en-CA', CurrencyCode: 'CAD' }],
    ['en-IE', { Id: 37, Text: 'English (Ireland)', Name: 'en-IE', CurrencyCode: 'EUR' }],
    ['en-JM', { Id: 38, Text: 'English (Jamaica)', Name: 'en-JM', CurrencyCode: 'JMD' }],
    ['en-NZ', { Id: 39, Text: 'English (New Zealand)', Name: 'en-NZ', CurrencyCode: 'NZD' }],
    ['en-PH', { Id: 40, Text: 'English (Republic of the Philippines)', Name: 'en-PH', CurrencyCode: 'PHP' }],
    ['en-ZA', { Id: 41, Text: 'English (South Africa)', Name: 'en-ZA', CurrencyCode: 'ZAR' }],
    ['en-TT', { Id: 42, Text: 'English (Trinidad and Tobago)', Name: 'en-TT', CurrencyCode: 'TTD' }],
    ['en-GB', { Id: 43, Text: 'English (United Kingdom)', Name: 'en-GB', CurrencyCode: 'GBP' }],
    ['en-US', { Id: 44, Text: 'English (United States)', Name: 'en-US', CurrencyCode: 'USD' }],
    ['en-ZW', { Id: 45, Text: 'English (Zimbabwe)', Name: 'en-ZW', CurrencyCode: 'USD' }],
    ['et-EE', { Id: 46, Text: 'Estonian (Estonia)', Name: 'et-EE', CurrencyCode: 'EUR' }],
    ['fo-FO', { Id: 47, Text: 'Faroese (Faroe Islands)', Name: 'fo-FO', CurrencyCode: 'DKK' }],
    ['fa-IR', { Id: 48, Text: 'Farsi (Iran)', Name: 'fa-IR', CurrencyCode: 'IRR' }],
    ['fi-FI', { Id: 49, Text: 'Finnish (Finland)', Name: 'fi-FI', CurrencyCode: 'EUR' }],
    ['fr-BE', { Id: 50, Text: 'French (Belgium)', Name: 'fr-BE', CurrencyCode: 'EUR' }],
    ['fr-CA', { Id: 51, Text: 'French (Canada)', Name: 'fr-CA', CurrencyCode: 'CAD' }],
    ['fr-FR', { Id: 52, Text: 'French (France)', Name: 'fr-FR', CurrencyCode: 'EUR' }],
    ['fr-LU', { Id: 53, Text: 'French (Luxembourg)', Name: 'fr-LU', CurrencyCode: 'EUR' }],
    ['fr-MC', { Id: 54, Text: 'French (Principality of Monaco)', Name: 'fr-MC', CurrencyCode: 'EUR' }],
    ['fr-CH', { Id: 55, Text: 'French (Switzerland)', Name: 'fr-CH', CurrencyCode: 'CHF' }],
    ['mk-MK', { Id: 56, Text: 'FYRO Macedonian (Former Yugoslav Republic of Macedonia)', Name: 'mk-MK', CurrencyCode: 'MKD' }],
    ['gl-ES', { Id: 57, Text: 'Galician (Galician)', Name: 'gl-ES', CurrencyCode: 'EUR' }],
    ['ka-GE', { Id: 58, Text: 'Georgian (Georgia)', Name: 'ka-GE', CurrencyCode: 'GEL' }],
    ['de-AT', { Id: 59, Text: 'German (Austria)', Name: 'de-AT', CurrencyCode: 'EUR' }],
    ['de-DE', { Id: 60, Text: 'German (Germany)', Name: 'de-DE', CurrencyCode: 'EUR' }],
    ['de-LI', { Id: 61, Text: 'German (Liechtenstein)', Name: 'de-LI', CurrencyCode: 'CHF' }],
    ['de-LU', { Id: 62, Text: 'German (Luxembourg)', Name: 'de-LU', CurrencyCode: 'EUR' }],
    ['de-CH', { Id: 63, Text: 'German (Switzerland)', Name: 'de-CH', CurrencyCode: 'CHF' }],
    ['el-GR', { Id: 64, Text: 'Greek (Greece)', Name: 'el-GR', CurrencyCode: 'EUR' }],
    ['gu-IN', { Id: 65, Text: 'Gujarati (India)', Name: 'gu-IN', CurrencyCode: 'INR' }],
    ['he-IL', { Id: 66, Text: 'Hebrew (Israel)', Name: 'he-IL', CurrencyCode: 'ILS' }],
    ['hi-IN', { Id: 67, Text: 'Hindi (India)', Name: 'hi-IN', CurrencyCode: 'INR' }],
    ['hu-HU', { Id: 68, Text: 'Hungarian (Hungary)', Name: 'hu-HU', CurrencyCode: 'HUF' }],
    ['is-IS', { Id: 69, Text: 'Icelandic (Iceland)', Name: 'is-IS', CurrencyCode: 'ISK' }],
    ['id-ID', { Id: 70, Text: 'Indonesian (Indonesia)', Name: 'id-ID', CurrencyCode: 'IDR' }],
    ['it-IT', { Id: 71, Text: 'Italian (Italy)', Name: 'it-IT', CurrencyCode: 'EUR' }],
    ['it-CH', { Id: 72, Text: 'Italian (Switzerland)', Name: 'it-CH', CurrencyCode: 'CHF' }],
    ['ja-JP', { Id: 73, Text: 'Japanese (Japan)', Name: 'ja-JP', CurrencyCode: 'JPY' }],
    ['kn-IN', { Id: 74, Text: 'Kannada (India)', Name: 'kn-IN', CurrencyCode: 'INR' }],
    ['kk-KZ', { Id: 75, Text: 'Kazakh (Kazakhstan)', Name: 'kk-KZ', CurrencyCode: 'KZT' }],
    ['kok-IN', { Id: 76, Text: 'Konkani (India)', Name: 'kok-IN', CurrencyCode: 'INR' }],
    ['ko-KR', { Id: 77, Text: 'Korean (Korea)', Name: 'ko-KR', CurrencyCode: 'KRW' }],
    ['lv-LV', { Id: 78, Text: 'Latvian (Latvia)', Name: 'lv-LV', CurrencyCode: 'EUR' }],
    ['lt-LT', { Id: 79, Text: 'Lithuanian (Lithuania)', Name: 'lt-LT', CurrencyCode: 'EUR' }],
    ['ms-BN', { Id: 80, Text: 'Malay (Brunei Darussalam)', Name: 'ms-BN', CurrencyCode: 'BND' }],
    ['en-MY', { Id: 81, Text: 'Malay (Malaysia)', Name: 'en-MY', CurrencyCode: 'MYR' }],
    ['mr-IN', { Id: 82, Text: 'Marathi (India)', Name: 'mr-IN', CurrencyCode: 'INR' }],
    ['mn-MN', { Id: 83, Text: 'Mongolian (Mongolia)', Name: 'mn-MN', CurrencyCode: 'MNT' }],
    ['nb-NO', { Id: 84, Text: 'Norwegian (Bokmål) (Norway)', Name: 'nb-NO', CurrencyCode: 'NOK' }],
    ['nn-NO', { Id: 85, Text: 'Norwegian (Nynorsk) (Norway)', Name: 'nn-NO', CurrencyCode: 'NOK' }],
    ['pl-PL', { Id: 86, Text: 'Polish (Poland)', Name: 'pl-PL', CurrencyCode: 'PLN' }],
    ['pt-BR', { Id: 87, Text: 'Portuguese (Brazil)', Name: 'pt-BR', CurrencyCode: 'BRL' }],
    ['pt-PT', { Id: 88, Text: 'Portuguese (Portugal)', Name: 'pt-PT', CurrencyCode: 'EUR' }],
    ['pa-IN', { Id: 89, Text: 'Punjabi (India)', Name: 'pa-IN', CurrencyCode: 'INR' }],
    ['ro-RO', { Id: 90, Text: 'Romanian (Romania)', Name: 'ro-RO', CurrencyCode: 'RON' }],
    ['ru-RU', { Id: 91, Text: 'Russian (Russia)', Name: 'ru-RU', CurrencyCode: 'RUB' }],
    ['sa-IN', { Id: 92, Text: 'Sanskrit (India)', Name: 'sa-IN', CurrencyCode: 'INR' }],
    ['sk-SK', { Id: 93, Text: 'Slovak (Slovakia)', Name: 'sk-SK', CurrencyCode: 'EUR' }],
    ['sl-SI', { Id: 94, Text: 'Slovenian (Slovenia)', Name: 'sl-SI', CurrencyCode: 'EUR' }],
    ['es-AR', { Id: 95, Text: 'Spanish (Argentina)', Name: 'es-AR', CurrencyCode: 'ARS' }],
    ['es-BO', { Id: 96, Text: 'Spanish (Bolivia)', Name: 'es-BO', CurrencyCode: 'BOB' }],
    ['es-CL', { Id: 97, Text: 'Spanish (Chile)', Name: 'es-CL', CurrencyCode: 'CLP' }],
    ['es-CO', { Id: 98, Text: 'Spanish (Colombia)', Name: 'es-CO', CurrencyCode: 'COP' }],
    ['es-CR', { Id: 99, Text: 'Spanish (Costa Rica)', Name: 'es-CR', CurrencyCode: 'CRC' }],
    ['es-DO', { Id: 100, Text: 'Spanish (Dominican Republic)', Name: 'es-DO', CurrencyCode: 'DOP' }],
    ['es-EC', { Id: 101, Text: 'Spanish (Ecuador)', Name: 'es-EC', CurrencyCode: 'USD' }],
    ['es-SV', { Id: 102, Text: 'Spanish (El Salvador)', Name: 'es-SV', CurrencyCode: 'USD' }],
    ['es-GT', { Id: 103, Text: 'Spanish (Guatemala)', Name: 'es-GT', CurrencyCode: 'GTQ' }],
    ['es-HN', { Id: 104, Text: 'Spanish (Honduras)', Name: 'es-HN', CurrencyCode: 'HNL' }],
    ['es-MX', { Id: 105, Text: 'Spanish (Mexico)', Name: 'es-MX', CurrencyCode: 'MXN' }],
    ['es-NI', { Id: 106, Text: 'Spanish (Nicaragua)', Name: 'es-NI', CurrencyCode: 'NIO' }],
    ['es-PA', { Id: 107, Text: 'Spanish (Panama)', Name: 'es-PA', CurrencyCode: 'PAB' }],
    ['es-PY', { Id: 108, Text: 'Spanish (Paraguay)', Name: 'es-PY', CurrencyCode: 'PYG' }],
    ['es-PE', { Id: 109, Text: 'Spanish (Peru)', Name: 'es-PE', CurrencyCode: 'PEN' }],
    ['es-PR', { Id: 110, Text: 'Spanish (Puerto Rico)', Name: 'es-PR', CurrencyCode: 'USD' }],
    ['es-ES', { Id: 111, Text: 'Spanish (Spain)', Name: 'es-ES', CurrencyCode: 'EUR' }],
    ['es-UY', { Id: 112, Text: 'Spanish (Uruguay)', Name: 'es-UY', CurrencyCode: 'UYU' }],
    ['es-VE', { Id: 113, Text: 'Spanish (Venezuela)', Name: 'es-VE', CurrencyCode: 'VES' }],
    ['sw-KE', { Id: 114, Text: 'Swahili (Kenya)', Name: 'sw-KE', CurrencyCode: 'KES' }],
    ['cu-KE', { Id: 115, Text: 'Swahili (Kenya) - Custom', Name: 'cu-KE', CurrencyCode: 'KES', MappedLocale: 'sw-KE' }],
    ['sv-FI', { Id: 116, Text: 'Swedish (Finland)', Name: 'sv-FI', CurrencyCode: 'EUR' }],
    ['sv-SE', { Id: 117, Text: 'Swedish (Sweden)', Name: 'sv-SE', CurrencyCode: 'SEK' }],
    ['syr-SY', { Id: 118, Text: 'Syriac (Syria)', Name: 'syr-SY', CurrencyCode: 'SYP' }],
    ['ta-IN', { Id: 119, Text: 'Tamil (India)', Name: 'ta-IN', CurrencyCode: 'INR' }],
    ['tt-RU', { Id: 120, Text: 'Tatar (Russia)', Name: 'tt-RU', CurrencyCode: 'RUB' }],
    ['te-IN', { Id: 121, Text: 'Telugu (India)', Name: 'te-IN', CurrencyCode: 'INR' }],
    ['tr-TR', { Id: 122, Text: 'Turkish (Turkey)', Name: 'tr-TR', CurrencyCode: 'TRY' }],
    ['uk-UA', { Id: 123, Text: 'Ukrainian (Ukraine)', Name: 'uk-UA', CurrencyCode: 'UAH' }],
    ['ur-PK', { Id: 124, Text: 'Urdu (Islamic Republic of Pakistan)', Name: 'ur-PK', CurrencyCode: 'PKR' }],
    ['vi-VN', { Id: 125, Text: 'Vietnamese (Viet Nam)', Name: 'vi-VN', CurrencyCode: 'VND' }],
    ['cu-SA', { Id: 126, Text: 'English (Saudi Arabia) - Custom', Name: 'cu-SA', CurrencyCode: 'SAR', MappedLocale: 'ar-SA' }],
    ['cu-PA', { Id: 127, Text: 'Spanish (Panama) - Custom', Name: 'cu-PA', CurrencyCode: 'PAB', MappedLocale: 'es-PA' }],
    ['cu-DO', { Id: 128, Text: 'Spanish (Dominican Republic) - Custom', Name: 'cu-DO', CurrencyCode: 'DOP', MappedLocale: 'es-DO' }]
]);

class BdxLocaleManagementService {
    cultureDefinition$;
    localeSubject$;
    constructor() {
        // default to en-US culture definition for load so we can be sure there is _always_
        // a culture definition available for the locale service to use
        const enUSCulture = CultureDefinitions.get('en-US');
        this.cultureDefinition$ = new BehaviorSubject(enUSCulture);
        this.localeSubject$ = new BehaviorSubject(enUSCulture.Name);
        this.localeSubject$
            .asObservable()
            .pipe(
        // skip the initial value
        skip(1), 
        // only react to locales that are truthy (non-blank string) that are different from the current culture definition's locale
        filter(locale => !!locale && locale !== this.locale), 
        // map to the culture definitions
        map(locale => this.lookupCultureDefinition(locale)), 
        // where it's defined
        filter(culture => culture !== undefined))
            .subscribe(culture => {
            this.cultureDefinition$.next(culture);
        });
    }
    /// ////////////////
    // Public Members //
    /// ////////////////
    /**
     * The current locale for the account, based on the current culture definition. Handles returning a mapped locale if
     * one exists, otherwise returns the culture definition's name.
     */
    get locale() {
        const currentCultureDefinition = this.cultureDefinition;
        return currentCultureDefinition.MappedLocale || currentCultureDefinition.Name;
    }
    /**
     * The current culture definition loaded for the account.
     */
    get cultureDefinition() {
        return this.cultureDefinition$.value;
    }
    /// ////////////////
    // Public Methods //
    /// ////////////////
    /**
     * Retrieve an observable that emits the current culture definition. This can be used to subscribe to changes in the culture definition.
     * @returns An observable that emits the current culture definition.
     */
    getCultureDefinitionObservable() {
        return this.cultureDefinition$.asObservable();
    }
    /**
     * Retrieve an observable that emits the current locale. This can be used to subscribe to changes in the locale.
     * @returns An observable that emits the current locale.
     */
    getLocaleObservable() {
        return this.localeSubject$.asObservable();
    }
    /**
     * Look up a culture definition based on a provided locale value.
     * @param locale The locale to get the culture definition for.
     * @returns The culture definition for the specified locale, or undefined if not found.
     */
    lookupCultureDefinition(locale) {
        if (!locale) {
            return undefined;
        }
        return CultureDefinitions.get(locale);
    }
    /**
     * Set the current locale for the account. This will trigger a change in the culture definition if the new locale is valid.
     * @param newLocale The new locale to set. If the locale is invalid, it will default to 'en-US'.
     */
    setLocale(newLocale) {
        if (!newLocale || !CultureDefinitions.has(newLocale)) {
            console.warn(`Invalid locale provided ${newLocale}, no changes made.`);
            return;
        }
        Globalize.culture(newLocale);
        this.localeSubject$.next(newLocale);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleManagementService, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleManagementService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleManagementService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [] });

/**
 * Service responsible for providing locale-related information.
 */
class BdxLocaleService {
    localeManagementService;
    constructor(localeManagementService) {
        this.localeManagementService = localeManagementService;
    }
    /// ////////////////
    // Public Methods //
    /// ////////////////
    /**
     * Formats a currency value based on the user's locale and configuration values for individual use cases. Internally uses `Globalize.format`.
     *
     * @param value The currency value to format.
     * @param precision The number of decimal places to display. Defaults to `2`.
     * @param locale The locale to use for formatting. If not provided, the global account locale will be used.
     * @returns The formatted currency value.
     */
    formatCurrency(value, precision = 2, overrideLocale) {
        const valueToUse = value?.toString() ?? '';
        const numberToUse = parseFloat(valueToUse);
        const localeToUse = overrideLocale || this.localeManagementService.locale;
        return Globalize.format(numberToUse, `c${(precision ?? '')}`, localeToUse);
    }
    /**
     * Formats a number value based on the user's locale and configuration values for individual use cases. Internally uses `Globalize.format`.
     *
     * @param value The number value to format.
     * @param precision The number of decimal places to display. Defaults to `0`.
     * @param locale The locale to use for formatting. If not provided, the global account locale will be used.
     * @returns The formatted number value.
     */
    formatNumber(value, precision = 0, locale) {
        const valueToUse = value?.toString() ?? '';
        const numberTouse = parseFloat(valueToUse);
        const localeToUse = locale || this.localeManagementService.locale;
        return Globalize.format(numberTouse, `N${precision ?? ''}`, localeToUse);
    }
    /**
     * Parses a string value into a number based on the user's locale. Internally uses `Globalize.parseFloat`.
     *
     * @param value The string value to parse as a number.
     * @param locale The locale to use for parsing. If not provided, the global account locale will be used.
     * @returns The parsed number value.
     */
    parseNumber(value, locale) {
        const localeToUse = locale || this.localeManagementService.locale;
        return Globalize.parseFloat(value, localeToUse);
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleService, deps: [{ token: BdxLocaleManagementService }], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleService, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxLocaleService, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }], ctorParameters: () => [{ type: BdxLocaleManagementService }] });

/**
 * Pipe to format currency values based on a user's account locale. Internally uses the `BdxLocaleService` to format the value.
 */
class BdxCurrencyPipe {
    localeManagementService;
    localeService;
    /**
     * Internal subject to track any changes to parameters passed to each individual instance of the pipe.
     */
    pipeParamChanged$ = new Subject();
    constructor(localeManagementService, localeService) {
        this.localeManagementService = localeManagementService;
        this.localeService = localeService;
    }
    /**
     * Transforms a value into a formatted currency string.
     *
     * @param value The currency value to format.
     * @param precision The number of decimal places to display. Defaults to `2`.
     * @param locale Optional string that specifies the locale to use for formatting. If not provided will fallback to the default beheavior of `BdxLocaleService`.
     */
    transform(value, precision = 2, locale) {
        // if there was an existing subscription, complete it to avoid memory leaks
        // this is necessary because if the pipe is called on value changes we need
        // to ensure that the previous subscription is cleaned up
        this.pipeParamChanged$.next();
        this.pipeParamChanged$.complete();
        // reset the subject for the next subscription
        this.pipeParamChanged$ = new Subject();
        // return a new subscription that will pipe out new values
        // immediately if the account locale is changed
        return this.localeManagementService
            .getLocaleObservable()
            .pipe(map(() => this.localeService.formatCurrency(value, precision, locale)), takeUntil(this.pipeParamChanged$));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxCurrencyPipe, deps: [{ token: BdxLocaleManagementService }, { token: BdxLocaleService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: BdxCurrencyPipe, isStandalone: true, name: "bdxCurrency" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxCurrencyPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'bdxCurrency',
                    pure: true,
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: BdxLocaleManagementService }, { type: BdxLocaleService }] });

/**
 * Pipe to format number values based on a user's account locale. Internally uses the `BdxLocaleService` to format the value.
 */
class BdxDecimalPipe {
    localeManagementService;
    localeService;
    /**
     * Internal subject to track any changes to parameters passed to each individual instance of the pipe.
     */
    pipeParamChanged$ = new Subject();
    constructor(localeManagementService, localeService) {
        this.localeManagementService = localeManagementService;
        this.localeService = localeService;
    }
    /**
     * Transforms a value into a formatted decimal string.
     *
     * @param value The number value to format.
     * @param precision The number of decimal places to display. Defaults to `0`.
     * @param locale Optional string that specifies the locale to use for formatting. If not provided will fallback to the default beheavior of `BdxLocaleService`.
     */
    transform(value, precision = 0, locale) {
        // if there was an existing subscription, complete it to avoid memory leaks
        // this is necessary because if the pipe is called on value changes we need
        // to ensure that the previous subscription is cleaned up
        this.pipeParamChanged$.next();
        this.pipeParamChanged$.complete();
        // reset the subject for the next subscription
        this.pipeParamChanged$ = new Subject();
        // return a new subscription that will pipe out new values
        // immediately if the account locale is changed
        return this.localeManagementService
            .getLocaleObservable()
            .pipe(map(() => this.localeService.formatNumber(value, precision, locale)), takeUntil(this.pipeParamChanged$));
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxDecimalPipe, deps: [{ token: BdxLocaleManagementService }, { token: BdxLocaleService }], target: i0.ɵɵFactoryTarget.Pipe });
    static ɵpipe = i0.ɵɵngDeclarePipe({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: BdxDecimalPipe, isStandalone: true, name: "bdxDecimal" });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: BdxDecimalPipe, decorators: [{
            type: Pipe,
            args: [{
                    name: 'bdxDecimal',
                    pure: true,
                    standalone: true
                }]
        }], ctorParameters: () => [{ type: BdxLocaleManagementService }, { type: BdxLocaleService }] });

class ErrorHash {
    // Hash function was repurposed from here http://werxltd.com/wp/2010/05/13/javascript-implementation-of-javas-string-hashcode-method/
    hashErrorString(value) {
        let hash = 0, i, chr, len;
        if (value.length === 0)
            return hash;
        for (i = 0, len = value.length; i < len; i++) {
            chr = value.charCodeAt(i);
            // eslint-disable-next-line no-bitwise
            hash = ((hash << 5) - hash) + chr;
            // eslint-disable-next-line no-bitwise
            hash |= 0; // Convert to 32bit integer
        }
        return hash;
    }
    static ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorHash, deps: [], target: i0.ɵɵFactoryTarget.Injectable });
    static ɵprov = i0.ɵɵngDeclareInjectable({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorHash, providedIn: 'root' });
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: ErrorHash, decorators: [{
            type: Injectable,
            args: [{ providedIn: 'root' }]
        }] });

/*
 * Public API Surface of ngx-app-utility
 */
// NOTE Keep alphabetical

/**
 * Generated bundle index. Do not edit.
 */

export { AppStabilityService, BdxCurrencyPipe, BdxDecimalPipe, BdxHttpModule, BdxLocaleManagementService, BdxLocaleService, CreditCardValidationService, CultureDefinitions, ErrorHash };
